import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ShareDialogPayload, ShareListItemData } from '@/types';
import { useDebounce } from '@/hooks/useDebounce';
import { useShareDialogUsers } from './hooks/useShareDialogUsers';

export function useShareDialog(open: boolean, payload: ShareDialogPayload | null) {
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [addedUsers, setAddedUsers] = useState<ShareListItemData[]>([]);
  const [itemChanges, setItemChanges] = useState<Record<string, Partial<ShareListItemData>>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activePayload, setActivePayload] = useState<ShareDialogPayload | null>(null);
  const [sendEmailNotification, setSendEmailNotification] = useState(false);

  const debouncedQuery = useDebounce(searchQuery, 300);

  const {
    sharedItems: fetchedShared,
    availableItems: fetchedAvailable,
    isLoading,
    error,
    refetch,
    objectId,
  } = useShareDialogUsers(open, payload);

  useEffect(
    function syncPayloadAndSearch() {
      if (!open) {
        setSearchQuery('');
        setActivePayload(null);
        setRemovedIds(new Set());
        setAddedUsers([]);
        setItemChanges({});
        setSendEmailNotification(false);
      } else {
        setActivePayload(payload);
      }
    },
    [open, payload],
  );

  // React "storing information from previous renders" pattern (equivalent to getDerivedStateFromProps).
  // setState during render is intentional — it triggers a synchronous re-render before paint, so
  // cached query data never flows through stale deltas when re-opening with a different object.
  // An effect would be one render too late.
  const prevObjectIdRef = useRef<string | null | undefined>(undefined);
  if (prevObjectIdRef.current !== undefined && prevObjectIdRef.current !== objectId) {
    prevObjectIdRef.current = objectId;
    setRemovedIds(new Set());
    setAddedUsers([]);
    setItemChanges({});
  } else {
    prevObjectIdRef.current = objectId;
  }

  const sharedItems = useMemo(
    () => [
      ...fetchedShared.filter((u) => !removedIds.has(u.id)).map((u) => ({ ...u, ...itemChanges[u.id] })),
      ...addedUsers.map((u) => ({ ...u, ...itemChanges[u.id] })),
    ],
    [fetchedShared, removedIds, addedUsers, itemChanges],
  );

  // Derived before availableItems so it can be reused in both availableItems and the return value.
  const revokedUsers = useMemo(() => fetchedShared.filter((u) => removedIds.has(u.id)), [fetchedShared, removedIds]);

  const availableItems = useMemo(() => {
    const addedIds = new Set(addedUsers.map((u) => u.id));
    return [
      ...fetchedAvailable.filter((u) => !addedIds.has(u.id)).map((u) => ({ ...u, ...itemChanges[u.id] })),
      ...revokedUsers.map((u) => ({ ...u, shareUnderlyingData: false })),
    ];
  }, [fetchedAvailable, addedUsers, revokedUsers, itemChanges]);

  const normalizedQuery = useMemo(() => debouncedQuery.trim().toLowerCase(), [debouncedQuery]);

  const searchSharedResults = useMemo(() => {
    if (!normalizedQuery) return [];
    return sharedItems.filter(
      (item) => item.name.toLowerCase().includes(normalizedQuery) || item.email.toLowerCase().includes(normalizedQuery),
    );
  }, [sharedItems, normalizedQuery]);

  const searchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    return availableItems.filter(
      (item) => item.name.toLowerCase().includes(normalizedQuery) || item.email.toLowerCase().includes(normalizedQuery),
    );
  }, [availableItems, normalizedQuery]);

  const isDirty = useMemo(() => {
    if (removedIds.size > 0 || addedUsers.length > 0) return true;
    // A key in itemChanges that matches the original fetched value doesn't count as dirty.
    return Object.entries(itemChanges).some(([id, changes]) => {
      const original = fetchedShared.find((u) => u.id === id);
      if (!original) return true;
      return Object.entries(changes).some(([key, value]) => original[key as keyof ShareListItemData] !== value);
    });
  }, [removedIds, addedUsers, itemChanges, fetchedShared]);

  const updateItem = useCallback((id: string, changes: Partial<ShareListItemData>) => {
    setItemChanges((prev) => ({ ...prev, [id]: { ...prev[id], ...changes } }));
  }, []);

  const addToShared = useCallback((item: ShareListItemData) => {
    if (item.previouslyShared) {
      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } else {
      setAddedUsers((prev) => (prev.some((u) => u.id === item.id) ? prev : [...prev, item]));
    }
  }, []);

  const removeFromShared = useCallback((item: ShareListItemData) => {
    if (item.previouslyShared) {
      setRemovedIds((prev) => new Set([...prev, item.id]));
      // Preserve itemChanges so re-adding a previouslyShared user restores their edits.
    } else {
      setAddedUsers((prev) => prev.filter((u) => u.id !== item.id));
      setItemChanges((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  }, []);

  return {
    isLoading,
    error,
    refetch,
    objectId,
    payload: activePayload,
    isDirty,
    sharedItems,
    availableItems,
    revokedUsers,
    searchSharedResults,
    searchResults,
    searchQuery,
    isSearchPending: searchQuery !== debouncedQuery,
    setSearchQuery,
    addToShared,
    removeFromShared,
    updateItem,
    sendEmailNotification,
    setSendEmailNotification,
  };
}
